import React, { Fragment, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Table, Card } from "react-bootstrap";
import styled, { css } from "styled-components";

import { combineFromCategories } from "_helpers";
import { reportsBehindOrdersActions } from "_actions";

import Spinner from "components/Spinner";

const createReportData = (reportData = []) => {
  return reportData?.reduce((res, item) => {
    const groupedRuleIndex = res.findIndex(({ category }) => category === item.category);

    if (groupedRuleIndex > -1) {
      res[groupedRuleIndex].total = res[groupedRuleIndex].total + item.count;
      res[groupedRuleIndex].rules.push({
        ...item.rule,
        count: item.count,
      });
    } else {
      res.push({
        category: item.category,
        rules: [{ ...item.rule, count: item.count }],
        total: item?.count,
      });
    }

    return res;
  }, []);
};

const StyledTable = styled(Table)`
  margin-bottom: 0;
`;

const TableRow = styled.tr`
  background-color: #dfdfdf;

  ${({ tableLayout }) =>
    tableLayout &&
    css`
      tablelayout: ${tableLayout};
    `}
`;
const TableMainRow = styled(TableRow)`
  background-color: #ffffff;
`;
const TableExpandedRow = styled(TableRow)`
  background-color: #ffffff;
`;
const CellHeader = styled.th`
  width: auto;

  ${({ width, minWidth }) => css`
    width: ${width};
    min-width: ${minWidth ? minWidth : 0};
  `}
`;
const Cell = styled.td`
  ${({ green }) =>
    green &&
    css`
      color: var(--green);
    `}

  ${({ red }) =>
    red &&
    css`
      color: var(--red);
    `}

    ${({ empty }) =>
    empty &&
    css`
      background-color: #f7f7f7;
      border-bottom: 1px solid #f7f7f7;
      color: #a5a5a5;
    `}

    ${({ borderLeft }) =>
    borderLeft &&
    css`
      border-left: 1px solid #dfdfdf;
    `}

    ${({ borderRight }) =>
    borderRight &&
    css`
      border-right: 1px solid #dfdfdf;
    `}

    ${({ borderBottom }) =>
    borderBottom &&
    css`
      border-bottom: 1px solid #dfdfdf;
    `}

    ${({ wordBreak }) =>
    wordBreak &&
    css`
      word-break: break-all;
      white-space: normal;
      padding-right: 24px !important;
    `}
`;

export default function BehindOrders() {
  const dispatch = useDispatch();
  const [expandedRows, setExpandedRows] = useState([]);
  const allProcessingErrors = useSelector(({ variants }) => variants.allProcessingErrors);
  const allProcessingErrorStatuses = useSelector(({ variants }) => variants.allProcessingErrorStatuses);
  const { loading, behindOrders } = useSelector(({ reportsBehindOrders }) => reportsBehindOrders);
  const report = useMemo(() => createReportData(behindOrders), [behindOrders]);

  const processingErrorsMap = combineFromCategories(allProcessingErrors);
  const processingErrorStatusesMap = combineFromCategories(allProcessingErrorStatuses);

  const handleGet = () => {
    dispatch(reportsBehindOrdersActions.getBehindOrders());
    setExpandedRows([]);
  };

  useEffect(() => {
    handleGet();
  }, []);

  return (
    <>
      <Container fluid className="mt-5 py-4">
        <Card>
          {!!loading && <Spinner />}
          {!loading && (
            <StyledTable responsive className="sales-by-domain-table">
              <thead>
                <TableMainRow tableLayout="fixed">
                  <CellHeader width="32px"></CellHeader>
                  <CellHeader width="auto" minWidth="120px">
                    Category
                  </CellHeader>
                  <CellHeader width="23%">Product(s)</CellHeader>
                  <CellHeader width="23%">Status(es)</CellHeader>
                  <CellHeader width="12%">Error</CellHeader>
                  <CellHeader width="12%">Error Status</CellHeader>
                  <CellHeader width="10%">Days Exceeded</CellHeader>
                  <CellHeader width="10%"># of Orders</CellHeader>
                </TableMainRow>
              </thead>
              <tbody>
                {!!report.length &&
                  report.map(({ category, rules, total }, idx) => {
                    return (
                      <Fragment key={`${category}-${idx}`}>
                        <TableRow tableLayout="fixed">
                          <Cell>
                            {rules.length && expandedRows.includes(`${idx}`) ? (
                              <i
                                className="fa fa-minus"
                                onClick={() => setExpandedRows([...expandedRows.filter((id) => id !== `${idx}`)])}
                              />
                            ) : (
                              <i className="fa fa-plus" onClick={() => setExpandedRows([...expandedRows, `${idx}`])} />
                            )}
                          </Cell>
                          <Cell>{category}</Cell>
                          <Cell />
                          <Cell />
                          <Cell />
                          <Cell />
                          <Cell />
                          <Cell red>{total}</Cell>
                        </TableRow>
                        {expandedRows.includes(`${idx}`) &&
                          rules.map((row, idx) => (
                            <TableExpandedRow key={`${idx}-${row.id}`}>
                              <Cell empty borderRight>
                                {idx + 1}
                              </Cell>
                              <Cell>{category}</Cell>
                              <Cell>
                                {row?.products?.map(({ code, customerName }) => (
                                  <Fragment key={code}>
                                    {customerName}
                                    <br />
                                  </Fragment>
                                ))}
                              </Cell>
                              <Cell>{row?.statuses?.join(", ")}</Cell>
                              <Cell>{processingErrorsMap[row?.processingError] || row?.processingError || "—"}</Cell>
                              <Cell>{processingErrorStatusesMap[row?.errorStatus] || row?.errorStatus || "—"}</Cell>
                              <Cell green>{row.timeToExceed}</Cell>
                              <Cell red>{row.count}</Cell>
                            </TableExpandedRow>
                          ))}
                      </Fragment>
                    );
                  })}
                {!report.length && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center" }}>
                      No Results
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledTable>
          )}
        </Card>
      </Container>
    </>
  );
}
